import {
  AxiosHeaders,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from "axios";
import apiClient, { requiresAuth } from "./apiClient";
import { Invitation, InvitationStatus } from "../models/Invitation";

export interface SendInvitationsResponse {
  message: string;
  numInvited: number;
  numErrors: number;
  errors: string[];
  failedInvitationEmails: string[];
}

class InvitationService {
  private createConfig(params: object = {}): InternalAxiosRequestConfig {
    const headers = new AxiosHeaders({});
    return {
      headers: headers as AxiosRequestHeaders,
      params,
    };
  }

  async listInvitations(
    courseId: string,
    status?: InvitationStatus
  ): Promise<[]> {
    // Apply the helper function to add the requiresAuth header
    const config = this.createConfig({ status });

    const response = await apiClient.get<[]>(
      `/courses/${courseId}/invitations`,
      requiresAuth(config)
    );
    return response.data;
  }

  async sendCourseInvitations(
    courseId: string
  ): Promise<SendInvitationsResponse> {
    const headers = new AxiosHeaders({});
    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
    };

    const response = await apiClient.post<SendInvitationsResponse>(
      `/courses/${courseId}/send-invitations`,
      null,
      requiresAuth(config)
    );
    return response.data;
  }

  async resendInvitation(courseId: string, invitationId: string): Promise<any> {
    const config = this.createConfig();
    const response = await apiClient.post(
      `/courses/${courseId}/invitations/${invitationId}/resend`,
      null,
      requiresAuth(config)
    );
    return response.data;
  }

  async revokeInvitation(courseId: string, invitationId: string): Promise<any> {
    const headers = new AxiosHeaders({});
    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
    };

    const response = await apiClient.post(
      `/courses/${courseId}/invitations/${invitationId}/revoke`,
      undefined,
      requiresAuth(config)
    );
    return response.data;
  }

  async uploadInvitationsExcel(
    courseId: string,
    file: File,
    expirationDate?: string
  ): Promise<any> {
    const headers = new AxiosHeaders({
      "Content-Type": "multipart/form-data",
    });
    //should we add the expiration date to the params if it is null?
    const params = expirationDate ? { expirationDate } : {};

    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
      params: params,
    };

    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post(
      `/courses/${courseId}/invitations/upload`,
      formData,
      requiresAuth(config)
    );
    return response.data;
  }

  async resendAllInvitations(courseId: string): Promise<any> {
    const headers = new AxiosHeaders({});
    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
    };

    const response = await apiClient.post(
      `/courses/${courseId}/invitations/resend-all`,
      undefined,
      requiresAuth(config)
    );
    return response.data;
  }

  async resendPendingInvitations(courseId: string): Promise<any> {
    const headers = new AxiosHeaders({});
    const config: InternalAxiosRequestConfig = {
      headers: headers as AxiosRequestHeaders,
    };

    const response = await apiClient.post(
      `/courses/${courseId}/invitations/resend-pending`,
      undefined,
      requiresAuth(config)
    );
    return response.data;
  }
}

export const invitationService = new InvitationService();
